import { useState } from "react";
import { api, naira, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Smartphone, Delete, PhoneCall } from "lucide-react";

const USSD_CODE = "*7006*99#";

export function UssdSimulator({ trigger }) {
  const [screen, setScreen] = useState("dial"); // dial | phone | menu | balance | findState | vendors | id | help | ussd
  const [input, setInput] = useState("");
  const [phone, setPhone] = useState("");
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  // ussd gateway session (Redeem Food / Cash Out via /api/ussd)
  const [ussdText, setUssdText] = useState("");
  const [ussdMsg, setUssdMsg] = useState("");
  const [ussdEnded, setUssdEnded] = useState(false);

  const reset = () => { setScreen("dial"); setInput(""); setPhone(""); setData(null); setVendors([]); setMsg(""); setUssdText(""); setUssdMsg(""); setUssdEnded(false); };

  const press = (k) => {
    if (k === "del") return setInput((s) => s.slice(0, -1));
    setInput((s) => s + k);
  };

  const dial = () => {
    if (input.replace(/\D/g, "") === "700699") { setScreen("phone"); setInput(""); }
    else setMsg("Invalid code. Dial *7006*99#");
  };

  const submitPhone = () => { setPhone(input); setInput(""); setScreen("menu"); };

  const loadBalance = async () => {
    setLoading(true); setMsg("");
    try {
      const { data } = await api.get("/public/balance", { params: { phone } });
      setData(data); setScreen("balance");
    } catch (e) { setMsg(formatApiError(e)); setScreen("menu"); }
    finally { setLoading(false); }
  };

  const loadVendors = async (state) => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendors", { params: state ? { state } : {} });
      setVendors(data.vendors.slice(0, 6)); setScreen("vendors");
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setLoading(false); }
  };

  // Drive the real /api/ussd gateway (same contract a live USSD provider will POST)
  const sendUssd = async (nextText) => {
    setLoading(true); setMsg("");
    try {
      const { data } = await api.post("/ussd/sim", { text: nextText });
      setUssdText(nextText);
      setUssdMsg(data.message);
      setUssdEnded(!data.continueSession);
      setScreen("ussd");
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const startUssd = (initial) => { setUssdEnded(false); sendUssd(initial); };

  const Body = () => {
    if (screen === "dial") return (
      <div className="text-emerald-300 font-mono text-sm">
        <p className="text-emerald-500 text-xs mb-2">TEXT-ME-FOOD USSD</p>
        <p>Enter service code and press call.</p>
        <div className="mt-4 text-2xl text-white min-h-[2rem]">{input || <span className="text-emerald-700">*7006*99#</span>}</div>
        {msg && <p className="text-red-400 text-xs mt-2">{msg}</p>}
      </div>
    );
    if (screen === "phone") return (
      <div className="text-emerald-300 font-mono text-sm">
        <p className="text-white">TMF: Enter your registered phone number</p>
        <div className="mt-4 text-xl text-white min-h-[1.5rem]">{input || <span className="text-emerald-700">080...</span>}</div>
        {msg && <p className="text-red-400 text-xs mt-2">{msg}</p>}
      </div>
    );
    if (screen === "menu") return (
      <div className="text-emerald-300 font-mono text-sm space-y-1">
        <p className="text-white">TEXT-ME-FOOD ({phone})</p>
        {["1. My Benefit / Balance", "2. Redeem Food (Paycode)", "3. Cash Out to Bank", "4. Find TMF Vendor", "5. My TMF ID", "6. Help"].map((m) => <p key={m}>{m}</p>)}
        {msg && <p className="text-red-400 text-xs mt-2">{msg}</p>}
        {loading && <p className="text-emerald-500">Loading…</p>}
      </div>
    );
    if (screen === "ussd") return (
      <div className="text-emerald-300 font-mono text-sm whitespace-pre-line">
        <p className="text-white">{ussdMsg}</p>
        {!ussdEnded && <div className="mt-3 text-lg text-white min-h-[1.5rem]">{input || <span className="text-emerald-700">reply…</span>}</div>}
        {ussdEnded && <p className="text-emerald-600 pt-2">0. Back to menu</p>}
        {msg && <p className="text-red-400 text-xs mt-2">{msg}</p>}
        {loading && <p className="text-emerald-500">…</p>}
      </div>
    );
    if (screen === "balance") return (
      <div className="text-emerald-300 font-mono text-xs space-y-1">
        <p className="text-white">TMF ID: {data?.tmf_id}</p>
        {(!data?.benefits || data.benefits.length === 0) && <p>No active benefits yet.</p>}
        {data?.benefits?.map((b) => (
          <div key={b.code}>
            <p>{b.programme}: {naira(b.balance)} [{b.status}]</p>
            <p className="text-emerald-600">{b.code}</p>
          </div>
        ))}
        <p className="text-emerald-600 pt-2">0. Back</p>
      </div>
    );
    if (screen === "findState") return (
      <div className="text-emerald-300 font-mono text-sm space-y-1">
        <p className="text-white">Find vendor:</p>
        <p>1. Lagos  2. Kano</p>
        <p>3. Rivers 4. FCT Abuja</p>
        <p>5. All states</p>
        <p className="text-emerald-600 pt-1">0. Back</p>
      </div>
    );
    if (screen === "vendors") return (
      <div className="text-emerald-300 font-mono text-xs space-y-1">
        <p className="text-white">Nearest TMF Vendors:</p>
        {vendors.length === 0 && <p>No vendors found.</p>}
        {vendors.map((v, i) => <p key={v.vendor_id}>{i + 1}. {v.business_name}, {v.lga || v.state}</p>)}
        <p className="text-emerald-600 pt-1">0. Back</p>
      </div>
    );
    if (screen === "id") return (
      <div className="text-emerald-300 font-mono text-sm space-y-1">
        <p className="text-white">Your TMF ID:</p>
        <p className="text-emerald-200 text-lg">{data?.tmf_id || "—"}</p>
        <p className="text-emerald-600 pt-2">0. Back</p>
      </div>
    );
    if (screen === "help") return (
      <div className="text-emerald-300 font-mono text-xs space-y-1">
        <p className="text-white">TMF Help</p>
        <p>Register on the web or at any TMF agent. Redeem Food gives you a Paycode to present at a Moniepoint agent. Cash Out sends money to your bank account.</p>
        <p className="text-emerald-600 pt-2">0. Back</p>
      </div>
    );
    return null;
  };

  const respond = () => {
    const v = input.trim(); setInput(""); setMsg("");
    if (screen === "ussd") {
      if (ussdEnded) { if (v === "0") setScreen("menu"); return; }
      const next = ussdText ? `${ussdText}*${v}` : v;
      return sendUssd(next);
    }
    if (screen === "balance" || screen === "id" || screen === "help") { if (v === "0") setScreen("menu"); return; }
    if (screen === "vendors") { if (v === "0") setScreen("findState"); return; }
    if (screen === "findState") {
      if (v === "0") return setScreen("menu");
      const map = { "1": "Lagos", "2": "Kano", "3": "Rivers", "4": "FCT Abuja", "5": "" };
      if (v in map) return loadVendors(map[v]);
      return;
    }
    if (screen === "menu") {
      if (v === "1") return loadBalance();
      if (v === "2") return startUssd("2");
      if (v === "3") return startUssd("3");
      if (v === "4") return setScreen("findState");
      if (v === "5") return (async () => { try { const { data } = await api.get("/public/balance", { params: { phone } }); setData(data); setScreen("id"); } catch (e) { setMsg(formatApiError(e)); } })();
      if (v === "6") return setScreen("help");
      setMsg("Invalid option");
    }
  };

  const onSend = () => {
    if (screen === "dial") return dial();
    if (screen === "phone") return submitPhone();
    return respond();
  };

  return (
    <Dialog onOpenChange={(o) => { if (!o) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xs p-0 bg-transparent border-0 shadow-none">
        <DialogTitle className="sr-only">TMF USSD Simulator (*7006*99#)</DialogTitle>
        <div className="mx-auto w-[280px] rounded-[2.5rem] bg-gray-900 p-3 shadow-2xl border-4 border-gray-800" data-testid="ussd-simulator">
          <div className="rounded-[1.8rem] bg-emerald-950 p-4 min-h-[220px]">
            <div className="flex items-center gap-1.5 text-emerald-500 text-[10px] mb-3">
              <Smartphone className="w-3 h-3" /> TMF · {USSD_CODE}
            </div>
            <Body />
          </div>
          <div className="grid grid-cols-3 gap-1.5 mt-3">
            {["1","2","3","4","5","6","7","8","9","*","0","#"].map((k) => (
              <button key={k} data-testid={`ussd-key-${k}`} onClick={() => press(k)} className="h-9 rounded-lg bg-gray-800 text-white font-mono hover:bg-gray-700 transition-colors">{k}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-1.5 mt-1.5">
            <button data-testid="ussd-del" onClick={() => press("del")} className="h-9 rounded-lg bg-gray-800 text-gray-300 flex items-center justify-center hover:bg-gray-700"><Delete className="w-4 h-4" /></button>
            <button data-testid="ussd-send" onClick={onSend} className="h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center hover:bg-emerald-500"><PhoneCall className="w-4 h-4" /></button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
